import React, { useState, useEffect } from 'react';
import { BrickCharts } from 'brickcharts/dist/brickcharts.es.js';

function HealthMonitor() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('http://localhost:3000/api/health');
        const healthData = await response.json();
        setHealth(healthData);
        setLastCheck(new Date());
      } catch (error) {
        setError(error.message);
        console.error('Health check error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkHealth();
    
    // Set up auto-refresh every 5 minutes if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(checkHealth, 5 * 60 * 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefresh]);

  const handleManualCheck = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('http://localhost:3000/api/health');
      const healthData = await response.json();
      setHealth(healthData);
      setLastCheck(new Date());
    } catch (error) {
      setError(error.message);
      console.error('Manual health check error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getServiceStatus = (service, status) => {
    const isHealthy = status === 'available' || status === true;
    return {
      name: service,
      status: isHealthy,
      icon: isHealthy ? '✅' : '❌',
      color: isHealthy ? 'healthy' : 'unhealthy'
    };
  };

  const getPerformanceMetrics = () => {
    if (!health) return null;

    return {
      responseTime: health.responseTime || 'N/A',
      cacheHitRate: health.cacheHitRate || 'N/A',
      uptime: health.uptime || 'N/A',
      memoryUsage: health.memoryUsage || 'N/A',
      activeConnections: health.activeConnections || 'N/A'
    };
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Checking service health...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h3>Health Check Error</h3>
        <p>{error}</p>
        <button 
          onClick={handleManualCheck} 
          className="retry-btn"
        >
          Try Again
        </button>
      </div>
    );
  }

  const performanceMetrics = getPerformanceMetrics();

  return (
    <div className="health-monitor">
      <div className="health-header">
        <h2>💚 Service Health Monitor</h2>
        <p>Real-time monitoring of BrickCharts service health and performance</p>
        
        <div className="health-controls">
          <button 
            onClick={handleManualCheck}
            disabled={loading}
            className="manual-check-btn"
          >
            🔄 Manual Check
          </button>
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>Auto-refresh (5min)</span>
          </label>
        </div>

        {lastCheck && (
          <p className="last-check">
            Last checked: {lastCheck.toLocaleTimeString()}
          </p>
        )}
      </div>

      {health && (
        <div className="health-content">
          <div className="service-status">
            <h3>🔧 Service Status</h3>
            <div className="status-grid">
              {Object.entries(health).map(([service, status]) => {
                if (typeof status === 'boolean') {
                  const serviceStatus = getServiceStatus(service, status);
                  return (
                    <div key={service} className={`health-item ${serviceStatus.color}`}>
                      <div className="service-info">
                        <span className="service-name">{serviceStatus.name}</span>
                        <span className="status-indicator">{serviceStatus.icon}</span>
                      </div>
                      <div className="service-details">
                        <span className="status-text">
                          {status ? 'Operational' : 'Down'}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>

          {performanceMetrics && (
            <div className="performance-metrics">
              <h3>📊 Performance Metrics</h3>
              <div className="metrics-grid">
                <div className="metric">
                  <span className="metric-label">Response Time</span>
                  <span className="metric-value">{performanceMetrics.responseTime}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Cache Hit Rate</span>
                  <span className="metric-value">{performanceMetrics.cacheHitRate}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Uptime</span>
                  <span className="metric-value">{performanceMetrics.uptime}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Memory Usage</span>
                  <span className="metric-value">{performanceMetrics.memoryUsage}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Active Connections</span>
                  <span className="metric-value">{performanceMetrics.activeConnections}</span>
                </div>
              </div>
            </div>
          )}

          <div className="health-summary">
            <h3>📋 Health Summary</h3>
            <div className="summary-stats">
              <div className="summary-stat">
                <span className="stat-label">Overall Status</span>
                <span className={`stat-value ${health.overall ? 'healthy' : 'unhealthy'}`}>
                  {health.overall ? '✅ Healthy' : '❌ Unhealthy'}
                </span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Services Online</span>
                <span className="stat-value">
                  {Object.values(health).filter(v => typeof v === 'boolean' && v).length} / {Object.values(health).filter(v => typeof v === 'boolean').length}
                </span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Last Updated</span>
                <span className="stat-value">
                  {lastCheck ? lastCheck.toLocaleString() : 'Unknown'}
                </span>
              </div>
            </div>
          </div>

          <div className="health-recommendations">
            <h3>💡 Recommendations</h3>
            <div className="recommendations-list">
              {health.overall ? (
                <div className="recommendation healthy">
                  <span className="rec-icon">✅</span>
                  <div className="rec-content">
                    <h4>All Systems Operational</h4>
                    <p>All services are running normally. No action required.</p>
                  </div>
                </div>
              ) : (
                <div className="recommendation unhealthy">
                  <span className="rec-icon">⚠️</span>
                  <div className="rec-content">
                    <h4>Service Issues Detected</h4>
                    <p>Some services may be experiencing issues. Check the service status above for details.</p>
                  </div>
                </div>
              )}
              
              {performanceMetrics && performanceMetrics.responseTime > 1000 && (
                <div className="recommendation warning">
                  <span className="rec-icon">🐌</span>
                  <div className="rec-content">
                    <h4>High Response Time</h4>
                    <p>Response time is above normal levels. Consider checking server load.</p>
                  </div>
                </div>
              )}

              {performanceMetrics && performanceMetrics.cacheHitRate < 50 && (
                <div className="recommendation warning">
                  <span className="rec-icon">💾</span>
                  <div className="rec-content">
                    <h4>Low Cache Hit Rate</h4>
                    <p>Cache efficiency is below optimal levels. Consider cache optimization.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!health && !loading && !error && (
        <div className="no-data">
          <p>No health data available</p>
          <p>Try refreshing the page or checking your connection.</p>
        </div>
      )}
    </div>
  );
}

export default HealthMonitor; 